/**
 * API client — prefers same-origin `/api` (Express/CRA proxy).
 * Set REACT_APP_API_BASE for direct Function calls in local debugging.
 */

import { getFirebaseAuth, waitForFirebaseAuth } from '@/lib/firebaseClient';

const API_BASE = (process.env.REACT_APP_API_BASE ?? "").replace(/\/$/, "");

/** Same-origin `/api` by default; absolute when REACT_APP_API_BASE is set. */
export function getApiBase() {
  return API_BASE;
}

function csrfHeader() {
  if (typeof document === "undefined") return {};
  const match = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((s) => s.startsWith("ls_csrf="));
  if (!match) return {};
  return { "X-CSRF-Token": decodeURIComponent(match.slice("ls_csrf=".length)) };
}

export function getToken() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("ls_token");
}

export function setToken(token) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem("ls_token", token);
  else localStorage.removeItem("ls_token");
  localStorage.removeItem("ls-token");
}

async function refreshFirebaseToken(force = true) {
  try {
    await waitForFirebaseAuth();
    const auth = getFirebaseAuth();
    if (!auth?.currentUser) return null;
    const fresh = await auth.currentUser.getIdToken(force);
    setToken(fresh);
    return fresh;
  } catch {
    return null;
  }
}

async function parseBody(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return text;
  }
}

export async function api(path, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 45000;
  const method = opts.method ?? "GET";
  let token = opts.token !== undefined ? opts.token : getToken();
  let retried = false;

  // Proactively refresh near-expiry tokens before the request when Firebase is ready.
  if (opts.token === undefined && token) {
    const fresh = await refreshFirebaseToken(false);
    if (fresh) token = fresh;
  }

  for (;;) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(method !== "GET" && method !== "HEAD" ? csrfHeader() : {}),
        },
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: controller.signal,
      });
      const data = await parseBody(res);
      if (res.status === 401 && !retried && opts.token === undefined) {
        const fresh = await refreshFirebaseToken(true);
        if (fresh) {
          token = fresh;
          retried = true;
          continue;
        }
      }
      return { status: res.status, data };
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error(`API timeout after ${timeoutMs}ms (${path})`);
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }
}

/**
 * Authenticated binary download — returns a Blob (or throws / returns JSON error).
 * @returns {{ status: number, blob?: Blob, data?: any, fileName?: string }}
 */
export async function apiBlob(path, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 120000;
  const method = opts.method ?? "GET";
  let token = opts.token !== undefined ? opts.token : getToken();
  let retried = false;

  if (opts.token === undefined && token) {
    const fresh = await refreshFirebaseToken(false);
    if (fresh) token = fresh;
  }

  for (;;) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: "include",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(method !== "GET" && method !== "HEAD" ? csrfHeader() : {}),
        },
        signal: controller.signal,
      });
      if (res.status === 401 && !retried && opts.token === undefined) {
        const fresh = await refreshFirebaseToken(true);
        if (fresh) {
          token = fresh;
          retried = true;
          continue;
        }
      }
      const contentType = res.headers.get("content-type") || "";
      if (!res.ok || contentType.includes("application/json")) {
        const data = await parseBody(res);
        return { status: res.status, data };
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      return { status: res.status, blob, fileName: match?.[1] };
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error(`API timeout after ${timeoutMs}ms (${path})`);
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }
}

/** Multipart upload (do not set Content-Type — browser sets boundary). */
export async function apiForm(path, formData, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 120000;
  const method = opts.method ?? "POST";
  let token = opts.token !== undefined ? opts.token : getToken();
  let retried = false;

  if (opts.token === undefined && token) {
    const fresh = await refreshFirebaseToken(false);
    if (fresh) token = fresh;
  }

  for (;;) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: "include",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...csrfHeader(),
        },
        body: formData,
        signal: controller.signal,
      });
      const data = await parseBody(res);
      if (res.status === 401 && !retried && opts.token === undefined) {
        const fresh = await refreshFirebaseToken(true);
        if (fresh) {
          token = fresh;
          retried = true;
          continue;
        }
      }
      return { status: res.status, data };
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error(`API timeout after ${timeoutMs}ms (${path})`);
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }
}
